import React, { useEffect, useRef } from 'react';
import { useFormContext, FormFieldsData } from 'framework/form';
import { useMessageService, InfoMessage } from 'framework/ui/Message';
import { Link } from '@mui/material';

const STORAGE_KEY_PREFIX = 'onestop-draft-';

const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const DEBOUNCE_MS = 1000;

type DraftEnvelope = {
  savedAt: number;
  data: FormFieldsData;
};

const storageKeyForRequest = (requestId: string): string => `${STORAGE_KEY_PREFIX}${requestId}`;

export const loadDraft = (requestId: string): { data: FormFieldsData; savedAt: number } | null => {
  try {
    const raw = localStorage.getItem(storageKeyForRequest(requestId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as DraftEnvelope;
    if (
      !parsed ||
      typeof parsed.savedAt !== 'number' ||
      !parsed.data ||
      typeof parsed.data !== 'object' ||
      Array.isArray(parsed.data)
    ) {
      localStorage.removeItem(storageKeyForRequest(requestId));
      return null;
    }
    if (Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(storageKeyForRequest(requestId));
      return null;
    }
    return { data: parsed.data, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
};

export const clearDraft = (requestId: string): void => {
  localStorage.removeItem(storageKeyForRequest(requestId));
};

const saveDraft = (requestId: string, data: FormFieldsData): void => {
  try {
    const envelope: DraftEnvelope = {
      savedAt: Date.now(),
      data,
    };
    localStorage.setItem(storageKeyForRequest(requestId), JSON.stringify(envelope));
  } catch {
    // quota / private mode
  }
};

const formatRestoredDate = (savedAt: number): string =>
  new Date(savedAt).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export type FormDraftManagerProps = {
  requestId: string;
  baselineInitialData: FormFieldsData;
  restoredFromSavedAt: number | null;
  messageContext?: string;
};

export const FormDraftManager: React.FC<FormDraftManagerProps> = ({
  requestId,
  baselineInitialData,
  restoredFromSavedAt,
  messageContext,
}) => {
  const { data, resetFormData } = useFormContext();
  const { addMessage, cleanMessage } = useMessageService(messageContext);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoreMessageRef = useRef<InfoMessage | null>(null);
  const hasShownRestoreRef = useRef(false);

  useEffect(() => {
    hasShownRestoreRef.current = false;
  }, [requestId]);

  useEffect(() => {
    if (restoredFromSavedAt == null || hasShownRestoreRef.current) {
      return;
    }
    hasShownRestoreRef.current = true;

    const dateLabel = formatRestoredDate(restoredFromSavedAt);

    const handleClearDraft = () => {
      clearDraft(requestId);
      if (restoreMessageRef.current) {
        cleanMessage(restoreMessageRef.current);
        restoreMessageRef.current = null;
      }
      resetFormData?.(baselineInitialData);
    };

    const content = (
      <span>
        We restored your incomplete request from {dateLabel}. To start fresh,{' '}
        <Link
          component="button"
          type="button"
          variant="body1"
          onClick={handleClearDraft}
          sx={{ verticalAlign: 'baseline' }}
        >
          clear saved draft
        </Link>
        .
      </span>
    );

    const msg = new InfoMessage(content);
    restoreMessageRef.current = msg;
    addMessage(msg);
  }, [restoredFromSavedAt, requestId, baselineInitialData, addMessage, cleanMessage, resetFormData]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      saveDraft(requestId, data as FormFieldsData);
      timeoutRef.current = null;
    }, DEBOUNCE_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, requestId]);

  return null;
};
